import React, { Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import { PropTypes } from 'prop-types';

import UrlBuilder from 'redpoints-connector-common/helper/UrlBuilder';

import {
  Carousel,
  Col,
  Field,
  Form,
  Info,
  Input,
  Paper,
  Row,
  Select,
} from 'redpoints-front-components';
import { NoResult, PriorityTag } from 'redpoints-front-common/components';

import { translate } from '../../../helpers/languageHelper';

import './reportedIncidents.less';

const FormItem = Form.Item;

const getGridFallbackImg = async () => {
  const relativeUrl = '/public/images/img_not_available_grid.png';
  return await UrlBuilder.getStaticBucketUrl(relativeUrl);
};

class ReportedIncidents extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      imageError: '',
      hasErrors: false,
    };

    this.handleValuesChange = this.handleValuesChange.bind(this);
    this.handleFieldsChange = this.handleFieldsChange.bind(this);
  }

  async componentDidMount() {
    const { current: form } = this.formRef;

    if (form) {
      const { onFormChange } = this.props;
      onFormChange(form.getFieldsValue());
    }

    const imageError = await getGridFallbackImg();

    this.setState({ imageError });
  }

  renderImageItem(image, index = 0) {
    const { imageError } = this.state;

    return (
      <div key={`key-${index}`}>
        <div
          style={{ backgroundImage: !image && `url('${imageError}')` }}
          className="reported-incidents-container__carousel__image-container"
        >
          <div
            className="reported-incidents-container__carousel__image"
            alt={image}
            key={`index-${index}`}
            style={{ backgroundImage: `url('${image ?? imageError}')` }}
          />
        </div>
      </div>
    );
  }

  handleValuesChange(changedValues) {
    const { current: form } = this.formRef;
    this.props.onFormChange(form, changedValues);
  }

  handleFieldsChange(_changedFields, allFields) {
    this.setState({
      hasErrors: allFields.some(({ errors }) => errors.length > 0),
    });
  }

  render() {
    const { formRef, state } = this;
    const { current: form } = formRef;
    const { hasErrors } = state;

    const {
      reportedIncidents,
      readOnly,
      children,
      filtersHaveBeenApplied,
      loadingData,
    } = this.props;

    if (!isEmpty(reportedIncidents)) {
      const {
        asset,
        assetList,
        contentLinkText,
        createdBy,
        detectionLink,
        images,
        priority,
        protectionType,
        protectionTypesList,
        title,
      } = reportedIncidents;

      const imageArrayHasData = Array.isArray(images) && images.length > 0;

      return (
        <Form
          layout="vertical"
          ref={formRef}
          className="reported-incidents-container"
          initialValues={{
            asset,
            protectionType,
            detectionLink,
            contentLinkText,
          }}
          onValuesChange={this.handleValuesChange}
          onFieldsChange={this.handleFieldsChange}
        >
          <Paper>
            <Row>
              <Col span={12}>
                <Row>
                  <Col span={16}>
                    <FormItem
                      name="asset"
                      rules={[
                        {
                          message: translate(
                            'incident.form.errors.fieldIsRequired'
                          ),
                          required: true,
                        },
                      ]}
                    >
                      <Select
                        data={assetList}
                        disabled={readOnly}
                        label={translate('incident.asset')}
                        placeholder={translate('incident.selectAnAsset')}
                        mapping={{ key: 'assetId', value: 'name' }}
                      />
                    </FormItem>
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col span={16}>
                    <Info
                      label={translate('incident.title')}
                      value={title}
                      disabled={!title}
                    />
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col span={16}>
                    <FormItem
                      name="protectionType"
                      rules={[
                        {
                          message: translate(
                            'incident.form.errors.fieldIsRequired'
                          ),
                          required: true,
                        },
                      ]}
                    >
                      <Select
                        data={protectionTypesList}
                        disabled={readOnly}
                        mapping={{
                          key: 'protectionType',
                          value: 'name',
                        }}
                        label={translate('incident.protectionType')}
                        placeholder={translate(
                          'incident.selectAnProtectionType'
                        )}
                      />
                    </FormItem>
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col span={16}>
                    <PriorityTag priority={priority} />
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col span={16}>
                    {readOnly ? (
                      <Info
                        disabled={readOnly}
                        label={translate('incident.detectionLink')}
                        value={
                          detectionLink ? (
                            <a
                              className="reported-incidents-container__anchor"
                              href={detectionLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {detectionLink}
                            </a>
                          ) : (
                            ''
                          )
                        }
                      />
                    ) : (
                      <FormItem name="detectionLink">
                        <Input
                          disabled={readOnly}
                          label={translate('incident.detectionLink')}
                        />
                      </FormItem>
                    )}
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col span={16}>
                    {readOnly ? (
                      <Info
                        disabled={readOnly}
                        label={translate('incident.contentLink')}
                        value={
                          contentLinkText ? (
                            <a
                              className="reported-incidents-container__anchor"
                              href={contentLinkText}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {contentLinkText}
                            </a>
                          ) : (
                            ''
                          )
                        }
                      />
                    ) : (
                      <FormItem name="contentLinkText">
                        <Input
                          disabled={readOnly}
                          label={translate('incident.contentLink')}
                        />
                      </FormItem>
                    )}
                  </Col>
                </Row>
                <Row className="reported-incidents-container__divider">
                  <Col
                    span={16}
                    className="rp-inline-block reported-incidents-container--disabled"
                  >
                    <Info
                      value={createdBy || ''}
                      label={translate('incident.createdBy')}
                      disabled={!createdBy}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row>
                  <Col span={16}>
                    <Field
                      rpElement="TextArea"
                      label={translate('incident.images')}
                      disabled={!imageArrayHasData}
                    >
                      {imageArrayHasData && (
                        <Carousel
                          className="reported-incidents-container__carousel"
                          lazyLoad="progressive"
                        >
                          {images.map((image, index) =>
                            this.renderImageItem(image, index)
                          )}
                        </Carousel>
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
            </Row>
            {children(form, hasErrors)}
          </Paper>
        </Form>
      );
    }

    if (filtersHaveBeenApplied && loadingData === false) {
      return (
        <div className="reported-incidents-panel-class">
          <NoResult
            text={translate('common.no_more_results')}
            subtext={translate('common.enjoy')}
          />
        </div>
      );
    }

    return <></>;
  }
}

ReportedIncidents.propTypes = {
  reportedIncidents: PropTypes.object,
  children: PropTypes.func,
  onFormChange: PropTypes.func,
  readOnly: PropTypes.bool,
  filtersHaveBeenApplied: PropTypes.bool,
};

ReportedIncidents.defaultProps = {
  reportedIncidents: null,
  children: () => {},
  onFormChange: () => {},
  readOnly: false,
  filtersHaveBeenApplied: false,
};

export default ReportedIncidents;
